import type React from 'react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cc from 'classcat'

import usePortalContainer from '@/hooks/usePortalContainer '

import styles from './tooltip.module.scss'

interface TooltipProps {
  content: string | React.ReactNode
  shown?: boolean
  position?: 'top' | 'bottom' | 'left' | 'right'
  children: React.ReactElement
  classNameContainer?: string
  classNameTooltip?: string
  persistent?: boolean
}

const Tooltip: React.FC<TooltipProps> = memo(
  ({
    content,
    position = 'top',
    children,
    shown = true,
    classNameContainer,
    classNameTooltip,
    persistent = false,
  }: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const portalContainer = usePortalContainer()

    const updatePosition = useCallback(() => {
      if (!containerRef.current || !tooltipRef.current) return

      void containerRef.current.offsetHeight
      const containerRect = containerRef.current.getBoundingClientRect()
      const tooltipEl = tooltipRef.current
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      const tooltipWidth = tooltipEl.offsetWidth
      const tooltipHeight = tooltipEl.offsetHeight
      const padding = 8

      const positions = {
        top: {
          top: containerRect.top - tooltipHeight - padding,
          left: containerRect.left + containerRect.width / 2 + 2,
          hasSpace: containerRect.top > tooltipHeight + padding,
        },
        bottom: {
          top: containerRect.bottom + padding,
          left: containerRect.left + containerRect.width / 2 + 2,
          hasSpace: containerRect.bottom + tooltipHeight + padding < viewportHeight,
        },
        left: {
          top: containerRect.top + containerRect.height / 2,
          left: containerRect.left - tooltipWidth - padding,
          hasSpace: containerRect.left > tooltipWidth + padding,
        },
        right: {
          top: containerRect.top + containerRect.height / 2,
          left: containerRect.right + padding,
          hasSpace: containerRect.right + tooltipWidth + padding < viewportWidth,
        },
      } as const

      // Determine best position
      const fallbackOrder: Record<typeof position, (keyof typeof positions)[]> = {
        top: ['top', 'bottom', 'right', 'left'],
        bottom: ['bottom', 'top', 'right', 'left'],
        left: ['left', 'right', 'top', 'bottom'],
        right: ['right', 'left', 'top', 'bottom'],
      }

      // Find the first position that has space
      const bestPosition =
        fallbackOrder[position].find((pos) => positions[pos].hasSpace) || position
      const { top, left } = positions[bestPosition]

      Object.assign(tooltipEl.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
      })

      tooltipEl.className = cc([
        styles.tooltip,
        styles[`tooltip__${bestPosition}`],
        classNameTooltip,
      ])
    }, [position, containerRef, tooltipRef, classNameTooltip])

    useEffect(() => {
      if (!isVisible) return

      let rafId: number

      const handleUpdate = () => {
        if (rafId) {
          cancelAnimationFrame(rafId)
        }

        rafId = requestAnimationFrame(() => {
          updatePosition()
        })
      }

      handleUpdate()

      window.addEventListener('scroll', handleUpdate, { passive: true, capture: true })
      window.addEventListener('resize', handleUpdate, { passive: true })
      document.addEventListener('wheel', handleUpdate, { passive: true, capture: true })

      const resizeObserver = new ResizeObserver(() => {
        updatePosition()
      })

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId)
        }
        window.removeEventListener('scroll', handleUpdate, { capture: true })
        window.removeEventListener('resize', handleUpdate)
        document.removeEventListener('wheel', handleUpdate, { capture: true })
        resizeObserver.disconnect()
      }
    }, [isVisible, position, updatePosition])

    if (!shown) return children

    return (
      <div
        ref={containerRef}
        className={cc([styles.tooltip__container, classNameContainer])}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => {
          if (!persistent) setIsVisible(false)
        }}
      >
        {children}
        {isVisible &&
          portalContainer &&
          createPortal(
            <div
              ref={tooltipRef}
              className={cc([styles.tooltip, styles[`tooltip__${position}`], classNameTooltip])}
            >
              {typeof content === 'string' ? (
                <div ref={contentRef} className={styles.tooltip__content}>
                  {content}
                </div>
              ) : (
                <div ref={contentRef}>{content}</div>
              )}
            </div>,
            portalContainer,
          )}
      </div>
    )
  },
)

Tooltip.displayName = 'Tooltip'

export default Tooltip
